import React, { useEffect, useState, useMemo } from 'react';
import { Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { createPublisher, updatePublisher } from '../../api/publisherApi';
import { cnpjMask, validaCNPJ } from '../../utils';
import { toast } from 'react-toastify';
import { Publisher } from '../../types/publisher';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { getAdNetworks } from '../../api/adNetworkApi';
import { useProfile } from '../../hooks/UserHooks';
import { Option } from '../../utils';

interface AddModalProps {
    publisher: Publisher | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Publisher) => void;
    onUpdateData: (newData: Publisher) => void;
}

const AddEditPublisherModal = ({ publisher, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const { userProfile } = useProfile();
    const [loading, setLoading] = useState<boolean>(false);
    const [adNetworks, setAdNetworks] = useState<any>([]);
    const [selectAdNetworkValue, setSelectAdNetworkValue] = useState<SingleValue<Option>>();
    const [selectManagerTypeValue, setSelectManagerTypeValue] = useState<SingleValue<Option>>();

    // Manager type options
    const managerTypeOptions = useMemo(() => [
        { value: 'account', label: 'Account' },
        { value: 'inventory', label: 'Inventory' }
    ], []);

    // Check if user can edit restricted fields
    const canEditRestrictedFields = userProfile?.role === 'ROLE_SUPER_ADMIN' || userProfile?.role === 'ROLE_NETWORK_ID';

    const toggleAddModal = () => {
        toggle();
        handleForm.resetForm();
    };

    useEffect(() => {
        if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
            getAdNetworks().then(setAdNetworks);
        }
    }, [userProfile?.role]);

    // Initialize manager type value when publisher changes
    useEffect(() => {
        if (publisher?.managerType) {
            const managerTypeOption = managerTypeOptions.find(option => option.value === publisher.managerType);
            setSelectManagerTypeValue(managerTypeOption);
        } else {
            setSelectManagerTypeValue(undefined);
        }
    }, [publisher?.managerType, managerTypeOptions]);

    const selectAdNetworkOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectAdNetworkValue(newValue);
        handleForm.setFieldValue('adNetworkId', newValue ? Number(newValue.value) : '');
    };

    const selectManagerTypeOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectManagerTypeValue(newValue);
        handleForm.setFieldValue('managerType', newValue ? newValue.value : '');
    };

    const handleAddSite = () => {
        const newSite = {
            mediaCad: '',
            cnpj: '',
            site: '',
            app: '',
            mediaKitUrl: '',
            mediaKitFile: '',
            accessibilityResources: false,
        };
        handleForm.setFieldValue('sites', [...handleForm.values.sites, newSite]);
    };

    const handleRemoveSite = (index: number) => {
        const sites = [...handleForm.values.sites];
        sites.splice(index, 1);
        handleForm.setFieldValue('sites', sites);
    };

    const handleAddUser = () => {
        const newUser = {
            name: '',
            email: '',
            birthday: '',
            jobTitle: '',
        };
        handleForm.setFieldValue('users', [...handleForm.values.users, newUser]);
    };

    const handleRemoveUser = (index: number) => {
        const users = [...handleForm.values.users];
        users.splice(index, 1);
        handleForm.setFieldValue('users', users);
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            adNetworkId: userProfile?.role === 'ROLE_SUPER_ADMIN' ? publisher?.adNetworkId || '' : undefined,
            id: publisher?.id || undefined,
            name: publisher?.name || '',
            managerType: publisher?.managerType || '',
            networkId: publisher?.networkId || '',
            adServerCurrency: publisher?.adServerCurrency || '',
            adNetworkPublisherId: publisher?.adNetworkPublisherId || '',
            sites: publisher?.sites?.length ? publisher.sites.map(site => ({
                id: site.id,
                mediaCad: site.mediaCad || '',
                document: site.document || '',
                site: site.site || '',
                app: site.app || '',
                mediaKitUrl: site.mediaKitUrl || '',
                accessibilityResources: site.accessibilityResources || false,
            })) : [{
                mediaCad: '',
                document: '',
                site: '',
                app: '',
                mediaKitUrl: '',
                accessibilityResources: false,
            }],
            users: publisher?.users?.length ? publisher.users.map(user => ({
                id: user.id,
                name: user.name || '',
                email: user.email || '',
                birthday: user.birthday || '',
                jobTitle: user.jobTitle || '',
            })) : [],
        },

        validationSchema: Yup.object({
            name: Yup.string().trim().required('Nome é obrigatório'),
            managerType: Yup.string().optional().nullable(),
            networkId: Yup.string().optional().nullable(),
            adServerCurrency: Yup.string().optional().nullable(),
            adNetworkPublisherId: Yup.string().optional().nullable(),
            sites: Yup.array()
                .of(
                    Yup.object({
                        mediaCad: Yup.number().integer().optional().nullable(),
                        document: Yup.string()
                            .optional()
                            .nullable()
                            .test('is-valid-cnpj', 'CNPJ inválido', value => value ? validaCNPJ(value) : true),
                        site: Yup.string()
                            .test(
                                'site-or-app',
                                'O campo site ou o campo app deve ser preenchido',
                                function (value) {
                                    const { app } = this.parent;
                                    return (!value && app) || (value && !app);
                                },
                            )
                            .test(
                                'site',
                                'Site inválido. Ex.: example.com',
                                function (value: any) {
                                    if (!value) {
                                        return true;
                                    }
                                    return value.match(/^(?=.{1,255}$)([a-z0-9-.+]{2,63}\.[a-z-]{2,10}\b)$/gi);
                                },
                            )
                            .nullable(),
                        app: Yup.string()
                            .test(
                                'app-or-site',
                                'O campo site ou o campo app deve ser preenchido',
                                function (value) {
                                    const { site } = this.parent;
                                    return (!value && site) || (value && !site);
                                },
                            )
                            .test(
                                'app',
                                'O ID do aplicativo deve ser um ID válido da Apple App Store (por exemplo, id1234567890) ou um ID da Google Play Store (por exemplo, com.example.app)',
                                function (value: any) {
                                    if (!value) {
                                        return true;
                                    }
                                    const appleAppStoreRegex = /^(id)?\d+$/;
                                    const googlePlayStoreRegex = /^[a-zA-Z0-9._-]+$/;
                                    return appleAppStoreRegex.test(value) || googlePlayStoreRegex.test(value);
                                },
                            )
                            .nullable(),
                        mediaKitUrl: Yup.string().nullable().optional().url().nullable(),
                        accessibilityResources: Yup.boolean().required('Recursos de acessibilidade é obrigatório'),
                    })
                )
                .min(1, 'Adicione pelo menos um site')
                .required('Sites é obrigatório'),
            users: Yup.array()
                .of(
                    Yup.object({
                        name: Yup.string().trim().required('Nome do usuário é obrigatório'),
                        email: Yup.string().trim().email('Email inválido').required('Email é obrigatório'),
                        birthday: Yup.string().optional().nullable(),
                        jobTitle: Yup.string().trim().optional().nullable(),
                    })
                )
                .optional()
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            
            // Prepare JSON data
            const publisherData = {
                adNetworkId: values.adNetworkId || undefined,
                id: values.id || undefined,
                name: values.name,
                managerType: values.managerType || undefined,
                networkId: values.networkId || undefined,
                adServerCurrency: values.adServerCurrency || undefined,
                adNetworkPublisherId: values.adNetworkPublisherId || undefined,
                sites: values.sites && Array.isArray(values.sites) ? values.sites.map((site: any) => ({
                    ...site,
                    id: site.id || undefined,
                    mediaCad: site.mediaCad || undefined,
                    document: site.document || undefined,
                    site: site.site || undefined,
                    app: site.app || undefined,
                    mediaKitUrl: site.mediaKitUrl || undefined,
                    accessibilityResources: site.accessibilityResources || false,
                })) : [],
                users: values.users && Array.isArray(values.users) && values.users.length > 0 ? values.users.map((user: any) => ({
                    ...user,
                    id: user.id || undefined,
                    name: user.name || undefined,
                    email: user.email || undefined,
                    birthday: user.birthday || undefined,
                    jobTitle: user.jobTitle || undefined,
                })) : []
            };
            if (publisher) {
                return updatePublisher(publisher.id, publisherData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Publisher atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createPublisher(publisherData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Publisher cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    })
        ;

    return (
        <Modal id="showModal" isOpen={isOpen} toggle={toggleAddModal} fade={false} keyboard={true}
            backdrop={false} centered size={'lg'}>
            <Form onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForm.handleSubmit();
                return false;
            }}>
                <ModalHeader toggle={toggleAddModal} className="bg-light p-3">
                    {publisher ? 'Editar' : 'Adicionar'} publisher
                </ModalHeader>

                <ModalBody>
                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                        <div className='row'>
                            <div className='col'>
                                <div className="mb-6">
                                    <Label
                                        htmlFor="adNetworkId"
                                        className="form-label"
                                    >
                                        Ad Network
                                    </Label>
                                    <Select
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable={true}
                                        isSearchable={true}
                                        name="adNetworkId"
                                        options={adNetworks && adNetworks.map((adNetwork: any) => {
                                            return { value: adNetwork.id, label: adNetwork.name };
                                        })}
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                borderColor: !!(handleForm.touched.adNetworkId && handleForm.errors.adNetworkId) ? 'red' : '#ced4da',
                                            }),
                                        }}
                                        onChange={selectAdNetworkOnChange}
                                        value={selectAdNetworkValue}
                                    />
                                    {handleForm.touched.adNetworkId && handleForm.errors.adNetworkId ? (
                                        <FormFeedback
                                            type="invalid"
                                            style={{ display: 'block' }}>{handleForm.errors.adNetworkId}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={'row'}>
                        <div className={'col'}>
                            <div className="mb-3">
                                <Label
                                    htmlFor="name"
                                    className="form-label"
                                >
                                    Nome do Publisher
                                </Label>
                                <Input
                                    className="form-control"
                                    type="text"
                                    id="name"
                                    name="name"
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    value={handleForm.values.name}
                                    invalid={
                                        !!(handleForm.touched.name && handleForm.errors.name)
                                    }
                                />
                                {handleForm.touched.name && handleForm.errors.name ? (
                                    <FormFeedback
                                        type="invalid">{handleForm.errors.name}</FormFeedback>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="managerType" className="form-label">
                                    Manager Type
                                </Label>
                                <Select
                                    options={managerTypeOptions}
                                    value={selectManagerTypeValue}
                                    onChange={selectManagerTypeOnChange}
                                    onBlur={() => handleForm.setFieldTouched('managerType', true)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Selecione o tipo de manager"
                                    isClearable
                                    isDisabled={!canEditRestrictedFields}
                                />
                                {handleForm.touched.managerType && handleForm.errors.managerType && (
                                    <div className="invalid-feedback d-block">{handleForm.errors.managerType}</div>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="networkId" className="form-label">
                                    Network ID
                                </Label>
                                <Input
                                    type="text"
                                    id="networkId"
                                    name="networkId"
                                    value={handleForm.values.networkId}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.networkId && handleForm.errors.networkId)}
                                    placeholder="Digite o Network ID"
                                    disabled={!canEditRestrictedFields}
                                />
                                {handleForm.touched.networkId && handleForm.errors.networkId && (
                                    <FormFeedback type="invalid">{handleForm.errors.networkId}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="adServerCurrency" className="form-label">
                                    Ad Server Currency
                                </Label>
                                <Input
                                    type="text"
                                    id="adServerCurrency"
                                    name="adServerCurrency"
                                    value={handleForm.values.adServerCurrency}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.adServerCurrency && handleForm.errors.adServerCurrency)}
                                    placeholder="Ex: USD, EUR, BRL"
                                    disabled={!canEditRestrictedFields}
                                />
                                {handleForm.touched.adServerCurrency && handleForm.errors.adServerCurrency && (
                                    <FormFeedback type="invalid">{handleForm.errors.adServerCurrency}</FormFeedback>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <Label htmlFor="adNetworkPublisherId" className="form-label">
                                    Ad Network Publisher ID
                                </Label>
                                <Input
                                    type="text"
                                    id="adNetworkPublisherId"
                                    name="adNetworkPublisherId"
                                    value={handleForm.values.adNetworkPublisherId}
                                    onChange={handleForm.handleChange}
                                    onBlur={handleForm.handleBlur}
                                    invalid={!!(handleForm.touched.adNetworkPublisherId && handleForm.errors.adNetworkPublisherId)}
                                    placeholder="ID do Publisher na Ad Network"
                                    readOnly
                                />
                                {handleForm.touched.adNetworkPublisherId && handleForm.errors.adNetworkPublisherId && (
                                    <FormFeedback type="invalid">{handleForm.errors.adNetworkPublisherId}</FormFeedback>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <hr className="my-4" />
                    <h5 className="mb-3">Sites</h5>

                    {handleForm.values.sites?.map((site: any, index: number) => (
                        <div key={index} className="border rounded p-3 mb-3" style={{ position: 'relative' }}>
                            {handleForm.values.sites.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    style={{ position: 'absolute', top: '10px', right: '10px' }}
                                    onClick={() => handleRemoveSite(index)}
                                >
                                    <i className="ri-delete-bin-line"></i>
                                </button>
                            )}
                            <h6 className="mb-3">Site #{index + 1}</h6>

                            <div className={'row'}>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`sites.${index}.mediaCad`}
                                            className="form-label"
                                        >
                                            Mídia Cad
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="text"
                                            id={`sites.${index}.mediaCad`}
                                            name={`sites.${index}.mediaCad`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={site.mediaCad}
                                            invalid={
                                                !!(handleForm.touched.sites?.[index]?.mediaCad && handleForm.errors.sites?.[index]?.mediaCad)
                                            }
                                        />
                                        {handleForm.touched.sites?.[index]?.mediaCad && handleForm.errors.sites?.[index]?.mediaCad ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.sites[index].mediaCad}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`sites.${index}.cnpj`}
                                            className="form-label"
                                        >
                                            CNPJ
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="text"
                                            id={`sites.${index}.cnpj`}
                                            name={`sites.${index}.cnpj`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={cnpjMask(site.cnpj)}
                                            invalid={
                                                !!(handleForm.touched.sites?.[index]?.cnpj && handleForm.errors.sites?.[index]?.cnpj)
                                            }
                                        />
                                        {handleForm.touched.sites?.[index]?.cnpj && handleForm.errors.sites?.[index]?.cnpj ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.sites[index].cnpj}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className={'row'}>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`sites.${index}.site`}
                                            className="form-label"
                                        >
                                            Site
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="text"
                                            id={`sites.${index}.site`}
                                            name={`sites.${index}.site`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={site.site}
                                            invalid={
                                                !!(handleForm.touched.sites?.[index]?.site && handleForm.errors.sites?.[index]?.site)
                                            }
                                        />
                                        {handleForm.touched.sites?.[index]?.site && handleForm.errors.sites?.[index]?.site ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.sites[index].site}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`sites.${index}.app`}
                                            className="form-label"
                                        >
                                            App
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="text"
                                            id={`sites.${index}.app`}
                                            name={`sites.${index}.app`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={site.app}
                                            invalid={
                                                !!(handleForm.touched.sites?.[index]?.app && handleForm.errors.sites?.[index]?.app)
                                            }
                                        />
                                        {handleForm.touched.sites?.[index]?.app && handleForm.errors.sites?.[index]?.app ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.sites[index].app}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col">
                                    <Label
                                        htmlFor={`sites.${index}.mediaKitUrl`}
                                        className="form-label"
                                    >
                                        Mídia Kit - URL
                                    </Label>
                                    {site.mediaKitUrl && (
                                        <a href={site.mediaKitUrl} target="_blank" rel="noreferrer">
                                            <i className={'ri-external-link-line'}></i>
                                        </a>
                                    )}
                                    <Input
                                        className="form-control"
                                        type="text"
                                        id={`sites.${index}.mediaKitUrl`}
                                        name={`sites.${index}.mediaKitUrl`}
                                        onChange={handleForm.handleChange}
                                        onBlur={handleForm.handleBlur}
                                        value={site.mediaKitUrl}
                                        invalid={
                                            !!(handleForm.touched.sites?.[index]?.mediaKitUrl && handleForm.errors.sites?.[index]?.mediaKitUrl)
                                        }
                                    />
                                    {handleForm.touched.sites?.[index]?.mediaKitUrl && handleForm.errors.sites?.[index]?.mediaKitUrl ? (
                                        <FormFeedback
                                            type="invalid">{handleForm.errors.sites[index].mediaKitUrl}</FormFeedback>
                                    ) : null}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col">
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`sites.${index}.accessibilityResources`}
                                            className="form-label"
                                        >
                                            Recursos de acessibilidade
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="checkbox"
                                            id={`sites.${index}.accessibilityResources`}
                                            name={`sites.${index}.accessibilityResources`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            checked={site.accessibilityResources}
                                            invalid={
                                                !!(handleForm.touched.sites?.[index]?.accessibilityResources && handleForm.errors.sites?.[index]?.accessibilityResources)
                                            }
                                        />
                                        {handleForm.touched.sites?.[index]?.accessibilityResources && handleForm.errors.sites?.[index]?.accessibilityResources ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.sites[index].accessibilityResources}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="btn btn-primary mb-3"
                        onClick={handleAddSite}
                    >
                        <i className="ri-add-line"></i> Adicionar Site
                    </button>

                    <hr className="my-4" />
                    <h5 className="mb-3">Usuários <span className="text-muted">(Opcional)</span></h5>

                    {handleForm.values.users && handleForm.values.users.length > 0 ? handleForm.values.users.map((user: any, index: number) => (
                        <div key={index} className="border rounded p-3 mb-3" style={{ position: 'relative' }}>
                            {handleForm.values.users.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    style={{ position: 'absolute', top: '10px', right: '10px' }}
                                    onClick={() => handleRemoveUser(index)}
                                >
                                    <i className="ri-delete-bin-line"></i>
                                </button>
                            )}
                            <h6 className="mb-3">Usuário #{index + 1}</h6>

                            <div className={'row'}>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`users.${index}.name`}
                                            className="form-label"
                                        >
                                            Nome <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="text"
                                            id={`users.${index}.name`}
                                            name={`users.${index}.name`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.name}
                                            invalid={
                                                !!(handleForm.touched.users?.[index]?.name && handleForm.errors.users?.[index]?.name)
                                            }
                                        />
                                        {handleForm.touched.users?.[index]?.name && handleForm.errors.users?.[index]?.name ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.users[index].name}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`users.${index}.email`}
                                            className="form-label"
                                        >
                                            Email <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="email"
                                            id={`users.${index}.email`}
                                            name={`users.${index}.email`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.email}
                                            invalid={
                                                !!(handleForm.touched.users?.[index]?.email && handleForm.errors.users?.[index]?.email)
                                            }
                                        />
                                        {handleForm.touched.users?.[index]?.email && handleForm.errors.users?.[index]?.email ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.users[index].email}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className={'row'}>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`users.${index}.birthday`}
                                            className="form-label"
                                        >
                                            Data de Nascimento
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="date"
                                            id={`users.${index}.birthday`}
                                            name={`users.${index}.birthday`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.birthday}
                                            invalid={
                                                !!(handleForm.touched.users?.[index]?.birthday && handleForm.errors.users?.[index]?.birthday)
                                            }
                                        />
                                        {handleForm.touched.users?.[index]?.birthday && handleForm.errors.users?.[index]?.birthday ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.users[index].birthday}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={'col'}>
                                    <div className="mb-3">
                                        <Label
                                            htmlFor={`users.${index}.jobTitle`}
                                            className="form-label"
                                        >
                                            Cargo
                                        </Label>
                                        <Input
                                            className="form-control"
                                            type="text"
                                            id={`users.${index}.jobTitle`}
                                            name={`users.${index}.jobTitle`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.jobTitle}
                                            invalid={
                                                !!(handleForm.touched.users?.[index]?.jobTitle && handleForm.errors.users?.[index]?.jobTitle)
                                            }
                                        />
                                        {handleForm.touched.users?.[index]?.jobTitle && handleForm.errors.users?.[index]?.jobTitle ? (
                                            <FormFeedback
                                                type="invalid">{handleForm.errors.users[index].jobTitle}</FormFeedback>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div className="text-center text-muted py-3">
                            <p>Nenhum usuário adicionado. Clique em "Adicionar Usuário" para adicionar usuários ao publisher.</p>
                        </div>
                    )}

                    <button
                        type="button"
                        className="btn btn-primary mb-3"
                        onClick={handleAddUser}
                    >
                        <i className="ri-add-line"></i> Adicionar Usuário
                    </button>
                </ModalBody>
                <ModalFooter>
                    <button
                        type="submit"
                        className="btn btn-success btn-label right ms-auto nexttab nexttab"
                    >
                        <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2"></i>
                        {loading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        {publisher ? 'Atualizar' : 'Cadastrar'}
                    </button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddEditPublisherModal;